/** @module csi/threeD/ViewerController */
define([
	"dojo/_base/declare",
	"csi/action/Controller",
	"csi/locale",
	"csi/model/document",
	"csi/reflection",
	"csi/service/rightPaneService",
	"csi/state/rootStore",
	"csi/state/3D/threeDViewerStore",
	"csi/service/nodeService",
	"csi/threeD/utility/snapshotsSuggestedTags",
], function (
	declare,
	Controller,
	locale,
	{ TYPE_MATERIALDRAPE },
	reflection,
	rightPaneService,
	{ rootStore },
	{ ThreeDStore },
	{ resolveNodesWithDependencies },
	{ getSnapshotSuggestedNodes, filterSnapshotSaveNodes }	
) {
	var THREEDVIEWER = "3DViewer";
	var THREEDMATERIALVIEWER = "3DMaterialViewer";

	return declare(Controller, {
		prepend: true,
		typeNode: reflection.TYPE_IMAGE,

		/**
		 * @override
		 * Ensure access to colorways
		 */
		onDependencyPaths(paths) {
			paths.push("Child:__Parent__/Child:ActiveColorways");
		},

		/**
		 * Return document subType
		 * @param {C8Node} - node
		 */
		_getDocumentSubtype: function (node) {
			var ctx = this.getContext();
			var parentNode = ctx.cache.getNode(node.__Parent__);
			return parentNode ? parentNode.Subtype : false;
		},

		onUploadSnapshot(snapshotData) {
			this.widget.refreshData();
		},

		/**
		 * Return document node
		 * @param {Context} - ctx
		 * @return {C8Node} - Node or false if no node
		 */
		getDocumentNode: function (ctx) {
			var node = ctx.getNode();

			if (!node) {
				return;
			}

			if (node.$Type == "Document" || node.$Type == TYPE_MATERIALDRAPE) {
				return node;
			} else if (node.Default3DMaterial) {
				var doc = ctx.cache.getNode(node.Default3DMaterial);
				if (doc && node.$Type == "ColorMaterial") {
					//m_urlParent of type ColorMaterial wont be able to save screenshots under Document revisions to need to rest the m_urlParent to latest revision url
					this.m_urlParent = doc.LatestRevision;
				}
				return doc;
			} else if (node.Default3DViewable) {
				return ctx.cache.getNode(node.Default3DViewable);
			} else if (node.$Type == "DocumentRevision") {
				var parentNode = ctx.getNodeParent();
				return parentNode;
			}
		},

		/**
		 * Returns document revision node
		 * @return {C8Node} node
		 */
		getDocumentRevision: function () {
			var ctx = this.getContext();
			var node = ctx.getNode();

			if (!node) {
				return null;
			}

			if (node.$Type == "Document") {
				return ctx.cache.getNode(node.LatestRevision);
			} else if (node.$Type == "Material" || node.$Type == "MaterialSample" || node.$Type == "ColorMaterial") {
				var doc = ctx.cache.getNode(node.Default3DMaterial);
				return ctx.cache.getNode(doc.LatestRevision);
			} else if (node.$Type == "Avatar") {
				var doc = ctx.cache.getNode(node.Default3DViewable);
				return ctx.cache.getNode(doc.LatestRevision);
			}

			return node;
		},

		/**
		 * Returns document root parent node
		 * @param {C8Node} docNode
		 * @return {C8Node} node
		 */
		getRootParentNode: async function (docNode) {
			const { m_cache } = this;
			const [docContainer] = await resolveNodesWithDependencies([docNode.__Parent__], ["Child:__Parent__/Child:ActiveColorways", "Child:__Parent__/Child:ProductColors"], m_cache);
			return m_cache.get(docContainer.__Parent__);
		},

		/**
		 * Returns node for viewer
		 * @return {object} node
		 */
		getViewerViewNode: function () {
			var ctx = this.getContext();
			var node = this.getDocumentRevision();

			if (!node) {
				var parentNode = ctx.getParent();
				if (parentNode.$Type == "MaterialBOM") {
					node = ctx.getNodeParent(parentNode);
				} else {
					return;
				}
			} else {
				//captured images are saved on parentUrl node
				this.m_urlParent = node.$URL;
			}

			return node;
		},

		/**
		 * Return Active Collorways from a style node
		 * @param {C8Node} styleNode
		 * @return {C8Node[]} array of nodes
		 */
		getActiveColorways(styleNode) {
			return this.m_cache.getNodes(styleNode?.ActiveColorways) || [];
		},

		/**
		 * Return Active Colored Materials from a material node
		 * @param {C8Node} materialNode
		 * @return {C8Node[]} array of nodes
		 */
		getActiveColorMaterials(materialNode) {
			return this.m_cache.getNodes(materialNode?.ProductColors);
		},

		/** 
		 * Return the Nodes that a snapshot can be saved to 
		 * @param {C8Node} rootNode
		 * @param {C8Node} docNode
		 * @return {SnapshotSaveLocationData} locations of array of nodes, and suggested locations array of strings
		 */
		async getSnapshotSaveNodes(rootNode, docNode) {
			const nodes = [];
			const docRevision = this.getDocumentRevision();
			const locationType = rootStore.routerStore.locationType;
			let activeColorways = [];
			const rootNodeHasImageContainer = reflection.getNodeClass(rootNode)?.isImageContainer;

			if (rootNodeHasImageContainer) {
				nodes.push(rootNode);
			}

			// if the root node is a style, get the active colorways. If the root node is a material, just add the material to the nodes
			if(rootNode?.$Type === "Style") {
				activeColorways = this.getActiveColorways(rootNode);
				nodes.push(...activeColorways);
			} else if(rootNode?.$Type === "Material") {
				const activeColorMaterials = this.getActiveColorMaterials(rootNode);
				nodes.push(...activeColorMaterials);
			}
			nodes.push(docRevision);

			const locations = filterSnapshotSaveNodes(nodes);
			if (!locations.length) {
				return {
					locations: [],
					suggestedLocations: []
				};
			}

			return {
				locations,
				suggestedLocations: rootNode ? getSnapshotSuggestedNodes(rootNode, activeColorways, docNode, locationType) : []
			};
		},

		/**
		 * Return Product Type 
		 * @param {C8Node} rootNode 
		 * @return {C8NodeType} Product Type if available
		 */
		getProductType(rootNode) {
			const productType = rootNode?.ProductType;
			if (productType) {
				return productType;
			} else if (rootNode?.$Type === "ColorMaterial") {
				// get the product type from the parent node of the ColorMaterial
				const parentNode = this.m_cache.get(rootNode.__Parent__);
				return parentNode?.ProductType;
			} 
			return null;
		},

		/**
		 * Open 3D viewer
		 */
		async process() {
			// When dealing with a MaterialDrape file, the docNode and revisionNode point to the MaterialDrape node and that is expected.
			var revisionNode = this.getViewerViewNode();
			var docNode;
			var rootNode;

			if (revisionNode.$Type == TYPE_MATERIALDRAPE) {
				docNode = revisionNode;
				rootNode = null;
			} else {
				docNode = this.m_cache.getNode(revisionNode.__Parent__);
				rootNode = await this.getRootParentNode(docNode);
			}

			/** To Do - handle this chcek when supporting 3d material viewers */
			const viewerType =
				(docNode && (docNode.Subtype !== reflection.URL_SUBTYPE_UNIFIED_3D_MATERIAL || revisionNode.U3M_IsGroup ? THREEDVIEWER : THREEDMATERIALVIEWER)) || THREEDVIEWER;
			
			const variantSource = this.m_cache.get(docNode.VariantSource);
			const variantSourceName = variantSource?.$Name;
			const threeDTitle = variantSourceName || docNode.$Name || locale.resource("NAME_EMPTY");
			const saveLocationsData = await this.getSnapshotSaveNodes(rootNode, docNode);
			const productType = this.getProductType(rootNode);
			const rootNodeType = rootNode?.$Type;
			const threeDStore = new ThreeDStore(docNode, revisionNode, this.onUploadSnapshot.bind(this), saveLocationsData, viewerType, this.m_cache, threeDTitle, variantSourceName, productType, rootNodeType);

			rightPaneService.open3dViewer(threeDStore);

			this.postProcess();
		},

		/**
		 * Sets the popup window title based on the type of viewer.
		 */
		getTitle: function () {
			if (this.popupView) {
				var popupId = this.popupView.defView.m_id;
				var title = this.m_action.title;

				if (!title) {
					var ctx = this.getContext();
					var doc = this.getDocumentNode(ctx);
					title = doc.$Name;
				}

				return locale.resource(popupId) + " - " + title;
			} else {
				return this.inherited(arguments);
			}
		},

		/**
		 * Provide defaults for file upload operation which is to use the document revision in context
		 * and special operation for upload
		 * NOTE: this can be overridden to change defaults
		 */
		onStart: function () {
			this.m_urlParent = this.m_urlNode;
			this.m_idAttrParent = reflection.ATTR_IMAGES;
			this.typeNode = reflection.TYPE_IMAGE;
			this.moduleName = "SiteAdmin";
			this.operationName = "MultiFilePublishWithImages";
		},

		/**
		 * @override
		 * Add CSS class to dialog
		 */
		onCreatePopupDialogParams: function (dialogParams) {
			dialogParams["class"] = "csi-dark-theme-popup csi-flex-dialog csi-3DMaterial-popup";
			dialogParams["bMaxSize"] = true;
		},

	});
});
